/*
  ALU Store — comportamiento del home rediseñado.
  Cada componente es un custom element, así funciona igual con varias instancias
  y cuando el Theme Editor re-renderiza una sección.
*/
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const define = (name, klass) => {
    if (!customElements.get(name)) customElements.define(name, klass);
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  const formatMoney = (amount) => `S/ ${Number(amount || 0).toFixed(2)}`;
  const icon = (paths) => `<svg class="alu-ph" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">${paths}</svg>`;
  const iconSearch = icon('<path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"/>');
  const iconCaret = icon('<path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"/>');

  /* ---------- Header: menú móvil y barra de categorías ---------- */
  define('alu-header', class extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('[data-menu-toggle]');
      this.panel = this.querySelector('[data-menu-panel]');
      this.nav = this.querySelector('[data-priority-nav]');

      if (this.toggleButton && this.panel) {
        this.toggleButton.addEventListener('click', () => this.toggleMenu(this.panel.hidden));
        this.panel.addEventListener('click', (event) => {
          if (event.target.closest('a')) this.toggleMenu(false);
        });
        this.onKey = (event) => {
          if (event.key === 'Escape' && !this.panel.hidden) {
            this.toggleMenu(false);
            this.toggleButton.focus();
          }
        };
        document.addEventListener('keydown', this.onKey);
      }

      /* Enlaces de la barra que no entran en el ancho: se ocultan desde el final. */
      if (this.nav && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.fitNav());
        this.resizeObserver.observe(this.nav);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      this.resizeObserver?.disconnect();
      document.body.style.overflow = '';
    }

    toggleMenu(open) {
      if (open) {
        const top = this.querySelector('.alu-header-top').getBoundingClientRect().bottom;
        this.style.setProperty('--alu-menu-top', `${Math.max(0, top)}px`);
      }
      this.panel.hidden = !open;
      this.toggleButton.setAttribute('aria-expanded', String(open));
      this.toggleButton.setAttribute('aria-label', open ? 'Cerrar menú' : 'Abrir menú');
      document.body.style.overflow = open ? 'hidden' : '';
    }

    fitNav() {
      const items = [...this.nav.querySelectorAll('[data-nav-item]')];
      if (!this.nav.offsetWidth) return;
      items.forEach((item) => { item.hidden = false; });
      for (let i = items.length - 1; i >= 0 && this.nav.scrollWidth > this.nav.clientWidth + 1; i -= 1) {
        items[i].hidden = true;
      }
    }
  });

  /* ---------- Buscador con sugerencias (Predictive Search de Shopify) ---------- */
  define('alu-predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.clearButton = this.querySelector('[data-search-clear]');
      if (!this.input || !this.results) return;

      this.syncClear();
      this.input.addEventListener('input', () => {
        this.syncClear();
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.search(), 220);
      });
      this.input.addEventListener('focus', () => {
        if (this.results.innerHTML && this.input.value.trim().length >= 2) this.open(true);
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.results.addEventListener('keydown', (event) => this.onKeydown(event));
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.syncClear();
        this.open(false);
        this.input.focus();
      });
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.open(false);
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      window.clearTimeout(this.timer);
    }

    syncClear() {
      if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
    }

    open(show) {
      this.results.hidden = !show;
      this.input.setAttribute('aria-expanded', String(show));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.open(false);
        this.input.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = [...this.results.querySelectorAll('a')];
      if (this.results.hidden || !links.length) return;
      event.preventDefault();
      const index = links.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      if (next < 0) this.input.focus();
      else links[Math.min(next, links.length - 1)].focus();
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.open(false);
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'query,product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const response = await fetch(`${root}search/suggest.json?${params}`, {
          signal: this.controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(response.status);
        const data = await response.json();
        this.render(term, data.resources?.results || {});
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[alu-header] Falló la búsqueda predictiva', error);
          this.open(false);
        }
      }
    }

    render(term, { queries = [], products = [] }) {
      const searchUrl = `${root}search?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;
      let html = '';
      if (queries.length) {
        html += '<ul>' + queries.map((query) =>
          `<li><a class="alu-search-query" href="${escapeHtml(query.url)}">${iconSearch}<span>${query.styled_text || escapeHtml(query.text)}</span></a></li>`
        ).join('') + '</ul>';
      }
      if (products.length) {
        html += '<ul class="alu-search-products">' + products.slice(0, 4).map((product) => {
          const image = product.featured_image?.url || product.image;
          const media = image
            ? `<img src="${escapeHtml(image)}${image.includes('?') ? '&' : '?'}width=128" alt="" width="64" height="64" loading="lazy">`
            : '<span class="alu-search-product-ph"></span>';
          return `<li><a class="alu-search-product" href="${escapeHtml(product.url)}">${media}<div><span>${escapeHtml(product.title)}</span><strong>${formatMoney(product.price)}</strong></div>${iconCaret}</a></li>`;
        }).join('') + '</ul>';
      }
      html += queries.length || products.length
        ? `<a class="alu-search-all" href="${searchUrl}">Ver todos los resultados para “${escapeHtml(term)}”</a>`
        : `<p class="alu-search-empty">No encontramos resultados para “${escapeHtml(term)}”.</p>`;
      this.results.innerHTML = html;
      this.open(document.activeElement === this.input || this.contains(document.activeElement));
    }
  });

  /* ---------- Mini-carrito del header (se carga al abrirlo) ---------- */
  define('alu-mini-cart', class extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-mini-cart-body]');
      if (!this.body) return;
      this.load = () => this.refresh();
      this.addEventListener('mouseenter', this.load);
      this.addEventListener('focusin', this.load);
    }

    async refresh() {
      if (this.loading || (this.loadedAt && Date.now() - this.loadedAt < 4000)) return;
      this.loading = true;
      try {
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        this.render(await response.json());
        this.loadedAt = Date.now();
      } catch (error) {
        console.error('[alu-header] No se pudo cargar el carrito', error);
      } finally {
        this.loading = false;
      }
    }

    render(cart) {
      if (!cart.item_count) {
        this.body.innerHTML = `<p class="alu-mini-cart-empty">Tu carrito está vacío.</p><a class="alu-button" href="${root}collections/all">Ver productos</a>`;
        return;
      }
      const items = cart.items.slice(0, 3).map((item) => {
        const media = item.image
          ? `<img src="${escapeHtml(item.image)}${item.image.includes('?') ? '&' : '?'}width=112" alt="" width="56" height="56" loading="lazy">`
          : '<span class="alu-search-product-ph"></span>';
        return `<li><a class="alu-mini-cart-item" href="${escapeHtml(item.url)}">${media}<div><span>${escapeHtml(item.product_title)}</span><small>${item.quantity} × ${formatMoney(item.final_price / 100)}</small></div></a></li>`;
      }).join('');
      const more = cart.items.length > 3 ? `<p class="alu-mini-cart-more">y ${cart.items.length - 3} producto(s) más</p>` : '';
      this.body.innerHTML =
        `<ul class="alu-mini-cart-items">${items}</ul>${more}` +
        `<p class="alu-mini-cart-total"><span>Subtotal</span><span>${formatMoney(cart.total_price / 100)}</span></p>` +
        `<a class="alu-button" href="${root}checkout">Finalizar compra</a>` +
        `<a class="alu-secondary-button" href="${root}cart">Ver carrito</a>`;
    }
  });

  /* ---------- Dropdown "Todas las categorías": cerrar al hacer clic fuera o con Escape ---------- */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-alu-dropdown][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-alu-dropdown][open]').forEach((details) => {
      details.removeAttribute('open');
      details.querySelector('summary')?.focus();
    });
  });

  /* ---------- Hero slider ----------
     Los banners con data-schedule solo entran a la rotación dentro de su horario
     (alu-schedule.js). Se revisa cada 20 s para que aparezcan o salgan sin recargar. */
  define('alu-hero-slider', class extends HTMLElement {
    connectedCallback() {
      this.allSlides = [...this.querySelectorAll('[data-hero-slide]')];
      this.allDots = [...this.querySelectorAll('[data-hero-dot]')];
      this.controls = this.querySelector('.alu-hero-controls');
      this.interval = Number(this.dataset.interval || 6) * 1000;
      this.autoplay = this.dataset.autoplay === 'true';
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.slides = [];
      this.dots = [];
      this.active = Math.max(0, this.allSlides.findIndex((slide) => slide.classList.contains('is-active')));

      this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.active - 1, true));
      this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.active + 1, true));
      this.allDots.forEach((dot) =>
        dot.addEventListener('click', () => {
          const index = this.slides.indexOf(this.allSlides[Number(dot.dataset.heroDot)]);
          if (index > -1) this.go(index, true);
        })
      );
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());

      // En el editor, seleccionar un bloque muestra su slide.
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('[data-hero-slide]'));
        if (index > -1) this.go(index);
        this.stop();
      });

      this.refresh();
      if (this.allSlides.some((slide) => slide.hasAttribute('data-schedule'))) {
        this.scheduleTimer = window.setInterval(() => this.refresh(), 20000);
      }
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      window.clearInterval(this.scheduleTimer);
    }

    isScheduledActive(slide) {
      if (!slide.hasAttribute('data-schedule')) return true;
      const check = window.ALUSchedule?.check(slide.dataset.schedule);
      if (this.designMode) {
        const chip = slide.querySelector('[data-schedule-chip]');
        if (chip) {
          chip.textContent = !check
            ? 'Programado'
            : check.errors.length && !check.rules.length
              ? 'Programado · revisa los horarios'
              : `Programado · ${check.active ? 'visible ahora' : 'fuera de horario'}${check.errors.length ? ' · hay líneas no válidas' : ''}`;
        }
        return true; // en el editor se ven todos
      }
      return Boolean(check?.active);
    }

    refresh() {
      const current = this.slides[this.active];
      const visible = this.allSlides.filter((slide) => this.isScheduledActive(slide));
      const changed = visible.length !== this.slides.length || visible.some((slide, i) => slide !== this.slides[i]);
      if (!changed) return;

      const added = visible.filter((slide) => !this.slides.includes(slide));
      this.allSlides.forEach((slide, i) => {
        const show = visible.includes(slide);
        slide.hidden = !show;
        if (this.allDots[i]) this.allDots[i].hidden = !show;
      });
      this.slides = visible;
      this.dots = this.allDots.filter((dot, i) => visible.includes(this.allSlides[i]));
      this.hidden = visible.length === 0;
      if (this.controls) this.controls.hidden = visible.length < 2;

      // Un banner programado que acaba de entrar va primero; si no, se mantiene el actual.
      const priority = this.designMode ? null : added.find((slide) => slide.hasAttribute('data-schedule-first'));
      const target = priority || (visible.includes(current) ? current : visible[0]);
      if (target) this.go(visible.indexOf(target));
      this.start();
    }

    go(index, restart = false) {
      if (!this.slides.length) return;
      this.active = (index + this.slides.length) % this.slides.length;
      this.allSlides.forEach((slide) => {
        const isActive = slide === this.slides[this.active];
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.querySelectorAll('a').forEach((link) => (link.tabIndex = isActive ? 0 : -1));
      });
      this.allDots.forEach((dot) => {
        const isActive = dot === this.dots[this.active];
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      });
      if (restart) this.start();
    }

    start() {
      this.stop();
      if (!this.autoplay || reduceMotion || this.slides.length < 2) return;
      this.timer = window.setInterval(() => this.go(this.active + 1), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
    }
  });

  /* ---------- Carrusel horizontal con flechas (categorías) ---------- */
  define('alu-scroller', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroller-track]');
      this.prev = this.querySelector('[data-scroller-prev]');
      this.next = this.querySelector('[data-scroller-next]');
      if (!this.track) return;

      this.prev?.addEventListener('click', () => this.scroll(-1));
      this.next?.addEventListener('click', () => this.scroll(1));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.hidden = max <= 0;
        if (this.next) this.next.hidden = max <= 0;
      };
      this.track.addEventListener('scroll', this.update, { passive: true });
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    scroll(direction) {
      const card = this.track.firstElementChild;
      if (!card) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      const max = this.track.scrollWidth - this.track.clientWidth;
      let target = this.track.scrollLeft + direction * step;
      // Al llegar a un extremo, vuelve al otro (carrusel circular).
      if (direction > 0 && this.track.scrollLeft >= max - 2) target = 0;
      if (direction < 0 && this.track.scrollLeft <= 2) target = max;
      this.track.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  });

  /* ---------- Cuenta regresiva ---------- */
  define('alu-countdown', class extends HTMLElement {
    connectedCallback() {
      this.end = Date.parse(this.dataset.end || '');
      this.box = this.closest('[data-countdown-box]') || this;
      if (Number.isNaN(this.end)) {
        this.box.hidden = true;
        return;
      }
      this.units = {
        days: this.querySelector('[data-countdown-days]'),
        hours: this.querySelector('[data-countdown-hours]'),
        minutes: this.querySelector('[data-countdown-minutes]'),
        seconds: this.querySelector('[data-countdown-seconds]'),
      };
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const remaining = Math.max(0, this.end - Date.now());
      if (remaining === 0) {
        window.clearInterval(this.timer);
        if (this.dataset.hideExpired === 'true') this.box.hidden = true;
      }
      const total = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(total / 86400),
        hours: Math.floor((total % 86400) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60,
      };
      Object.entries(values).forEach(([unit, value]) => {
        if (this.units[unit]) this.units[unit].textContent = String(value).padStart(2, '0');
      });
      this.setAttribute(
        'aria-label',
        `${values.days} días, ${values.hours} horas, ${values.minutes} minutos y ${values.seconds} segundos restantes`
      );
    }
  });

  /* ---------- Agregar al carrito sin salir del home ---------- */
  const updateCartCount = (cart) => {
    document.querySelectorAll('alu-mini-cart').forEach((miniCart) => { miniCart.loadedAt = 0; });
    document.querySelectorAll('[data-alu-cart-count]').forEach((badge) => {
      badge.textContent = cart.item_count;
      const link = badge.closest('a');
      if (link) link.setAttribute('aria-label', `Carrito, ${cart.item_count} productos`);
    });
  };
  const fetchCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  };
  const refreshCartCount = async () => {
    try {
      const cart = await fetchCart();
      updateCartCount(cart);
      return cart;
    } catch (error) {
      console.error('[alu-home] No se pudo actualizar el carrito', error);
      return null;
    }
  };

  /* Modal "Tu carrito": se abre al agregar un producto. Muestra el carrito real
     (cart.js) y permite cambiar cantidades o quitar productos (cart/change.js). */
  const iconClose = icon('<path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/>');
  const cartDialog = () => {
    let dialog = document.querySelector('.alu-cart-dialog');
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'alu-scope alu-cart-dialog';
    dialog.setAttribute('aria-labelledby', 'AluCartDialogTitle');
    dialog.innerHTML =
      '<header><h2 id="AluCartDialogTitle">Tu carrito</h2>' +
      `<button class="alu-cart-dialog-close" type="button" data-close aria-label="Cerrar carrito">${iconClose}</button></header>` +
      '<div class="alu-cart-dialog-items" data-cart-items></div>' +
      '<p class="alu-cart-dialog-error" data-cart-error role="alert" hidden></p>' +
      '<div class="alu-cart-dialog-footer" data-cart-footer>' +
      '<p class="alu-cart-dialog-total"><span>Subtotal</span><strong data-cart-subtotal></strong></p>' +
      '<p class="alu-cart-dialog-note">Envío calculado al finalizar la compra.</p>' +
      `<a class="alu-button alu-catalog-add" href="${root}checkout">Finalizar compra</a>` +
      '</div>' +
      '<button class="alu-cart-dialog-continue" type="button" data-close>Seguir explorando</button>';
    // El scroll se libera al cerrar (clic, Escape o el evento close, que en pestañas ocultas se demora).
    const unlock = () => { document.body.style.overflow = ''; };
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-close]')) {
        dialog.close();
        unlock();
      }
    });
    dialog.addEventListener('cancel', unlock);
    dialog.addEventListener('close', unlock);
    dialog.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-qty]');
      if (input) changeLine(input.dataset.cartQty, Math.max(0, Math.floor(Number(input.value) || 0)));
    });
    dialog.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) changeLine(remove.dataset.cartRemove, 0);
    });
    document.body.appendChild(dialog);
    return dialog;
  };

  const renderCart = (cart) => {
    const dialog = cartDialog();
    const list = dialog.querySelector('[data-cart-items]');
    dialog.querySelector('[data-cart-footer]').hidden = !cart.item_count;
    if (!cart.item_count) {
      list.innerHTML = '<p class="alu-cart-dialog-empty">Tu carrito está vacío.</p>';
      return;
    }
    dialog.querySelector('[data-cart-subtotal]').textContent = formatMoney(cart.total_price / 100);
    list.innerHTML = cart.items.map((item) => {
      const image = item.image
        ? `<img src="${escapeHtml(item.image)}${item.image.includes('?') ? '&' : '?'}width=160" alt="" width="65" height="65">`
        : '<span class="alu-cart-dialog-noimg"></span>';
      const variant = item.variant_title && !item.product_has_only_default_variant
        ? `<small>${escapeHtml(item.variant_title)}</small>` : '';
      const qtyId = `AluCartQty-${escapeHtml(item.key).replace(/[^\w-]/g, '')}`;
      return `<div class="alu-cart-dialog-item">` +
        `<a href="${escapeHtml(item.url)}">${image}</a>` +
        `<div><a class="alu-cart-dialog-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>${variant}` +
        `<p>${formatMoney(item.final_price / 100)}</p>` +
        `<div class="alu-cart-dialog-row"><label for="${qtyId}">Cantidad</label>` +
        `<input id="${qtyId}" type="number" min="0" inputmode="numeric" value="${item.quantity}" data-cart-qty="${escapeHtml(item.key)}" aria-label="Cantidad de ${escapeHtml(item.product_title)}">` +
        `<button type="button" data-cart-remove="${escapeHtml(item.key)}">Quitar</button></div></div></div>`;
    }).join('');
  };

  const changeLine = async (key, quantity) => {
    const dialog = cartDialog();
    const error = dialog.querySelector('[data-cart-error]');
    dialog.setAttribute('aria-busy', 'true');
    error.hidden = true;
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'No se pudo actualizar el carrito');
      renderCart(data);
      updateCartCount(data);
      document.dispatchEvent(new CustomEvent('alu:cart-updated'));
    } catch (err) {
      console.error('[alu-home]', err);
      error.textContent = err.message;
      error.hidden = false;
      const cart = await refreshCartCount();
      if (cart) renderCart(cart);
    } finally {
      dialog.removeAttribute('aria-busy');
    }
  };

  const openCart = (cart) => {
    const dialog = cartDialog();
    dialog.querySelector('[data-cart-error]').hidden = true;
    renderCart(cart);
    if (!dialog.open) {
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      dialog.querySelector('.alu-cart-dialog-close').focus({ preventScroll: true });
    }
  };

  /* Modal de error al agregar, centrado en la pantalla (uno por página). */
  let lastFocus;
  const closeToast = () => {
    const modal = document.querySelector('.alu-cart-modal');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.style.overflow = '';
    lastFocus?.focus?.({ preventScroll: true });
  };
  const showToast = (title, text, ok = true) => {
    let modal = document.querySelector('.alu-cart-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'alu-scope alu-cart-modal';
      modal.hidden = true;
      modal.innerHTML =
        '<div class="alu-cart-modal-card" role="dialog" aria-modal="true" aria-labelledby="AluCartModalTitle">' +
        '<button class="alu-icon-button alu-cart-modal-close" type="button" data-close aria-label="Cerrar">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button>' +
        '<span class="alu-cart-modal-icon" aria-hidden="true"></span>' +
        '<strong id="AluCartModalTitle"></strong><p></p>' +
        '<div class="alu-cart-modal-actions">' +
        `<a class="alu-button" href="${root}cart">Ver carrito</a>` +
        '<button class="alu-secondary-button" type="button" data-close>Seguir comprando</button>' +
        '</div></div>';
      modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-close]')) closeToast();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeToast();
      });
      document.body.appendChild(modal);
    }
    modal.classList.toggle('is-error', !ok);
    modal.querySelector('strong').textContent = title;
    modal.querySelector('p').textContent = text;
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    modal.querySelector(ok ? '.alu-button' : '[data-close]').focus({ preventScroll: true });
  };

  define('alu-add-to-cart', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.submit(event));
    }

    async submit(event) {
      // "Comprar ahora" hace el submit nativo (return_to=/checkout).
      if (event.submitter && event.submitter.hasAttribute('data-buy-now')) return;
      event.preventDefault();

      const button = event.submitter || this.form.querySelector('button[type="submit"]');
      const buttons = [button, ...document.querySelectorAll(`[form="${this.form.id}"][type="submit"]`)].filter(Boolean);
      // Si el botón tiene ícono, el texto vive en [data-button-label] y solo se cambia eso.
      const labelOf = (item) => item.querySelector('[data-button-label]') || item;
      const labels = buttons.map((item) => labelOf(item).textContent);
      buttons.forEach((item) => {
        item.disabled = true;
      });
      labelOf(button).textContent = 'Agregando…';

      try {
        const formData = new FormData(this.form);
        formData.delete('return_to');
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || 'No se pudo agregar');

        labelOf(button).textContent = 'Agregado ✓';
        const cart = await refreshCartCount();
        if (cart) openCart(cart);
        document.dispatchEvent(new CustomEvent('alu:cart-updated'));
      } catch (error) {
        console.error('[alu-home]', error);
        labelOf(button).textContent = 'Intenta de nuevo';
        showToast('No se pudo agregar', error.message, false);
      } finally {
        window.setTimeout(() => {
          buttons.forEach((item, i) => {
            labelOf(item).textContent = labels[i];
            item.disabled = item.hasAttribute('data-unavailable');
          });
        }, 1800);
      }
    }
  });

  /* ---------- Cupón: clic para copiar el código ---------- */
  define('alu-coupon', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.hint = this.querySelector('[data-coupon-hint]');
      if (!this.button) return;
      this.hintText = this.hint?.textContent;
      this.button.addEventListener('click', () => this.copy());
    }

    async copy() {
      const code = this.dataset.code;
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
        this.button.classList.add('is-copied');
        if (this.hint) this.hint.textContent = '¡Copiado!';
        window.setTimeout(() => {
          this.button.classList.remove('is-copied');
          if (this.hint) this.hint.textContent = this.hintText;
        }, 1800);
      } catch (error) {
        console.error('[alu-home] No se pudo copiar el cupón', error);
      }
    }
  });
})();
